import { all, AllEffect, CallEffect, put, PutEffect, select, SelectEffect, takeEvery, call } from 'redux-saga/effects';
import { Contract, ContractTransaction } from 'ethers';
import { abi as ERC721ABI } from '@niftyhorde/swap-kiwi-contracts/artifacts/@openzeppelin/contracts/token/ERC721/IERC721.sol/IERC721.json';
import { ERC721 } from '@niftyhorde/swap-kiwi-contracts/typechain/ERC721';

import { selectActiveSwap, selectSelectedNFTs } from '../app/selectors';
import { getIdsAndAddressesAndAmounts } from '../../utils/nft';
import { INFT } from '../../types/nft';
import { getKiwiContractAddress } from '../../hooks/contract/useContract';
import { AddressApproval } from './slice';
import { checkApprovals, makeApproval, storeCheckedApprovals } from './actions';
import { addPendingTransaction } from '../transactions/actions';
import { addErrorNotification } from '../../services/notifications';
import { ISwap } from '../../types/swap';

function* fetchUserSelectedNFTsApproval({
  payload: { signer, chainId },
}: ReturnType<typeof checkApprovals>): Generator<
  SelectEffect | CallEffect | AllEffect<Promise<boolean>> | Promise<string> | PutEffect,
  void,
  INFT[] & ISwap & string & boolean
> {
  const selectedNFTs = yield select(selectSelectedNFTs);
  const activeSwap = yield select(selectActiveSwap);
  const { addresses } = getIdsAndAddressesAndAmounts(selectedNFTs);
  const uniqueAddresses = Array.from(new Set(addresses));
  const kiwiContractAddress = getKiwiContractAddress(chainId, activeSwap as ISwap | undefined);

  const userAddress = yield signer.getAddress();

  const fetchPromises = uniqueAddresses.map((address) => {
    const contract = new Contract(address, ERC721ABI, signer) as ERC721;
    return contract.isApprovedForAll(userAddress, kiwiContractAddress);
  });
  const isContractApprovedList = yield all(fetchPromises);

  const approvals: AddressApproval = {};
  uniqueAddresses.map((address, index) => {
    approvals[address.toLowerCase()] = isContractApprovedList[index];
  });
  yield put(storeCheckedApprovals(approvals));
}

function* writeApproval({
  payload: { tokenAddress, signer, chainId },
  meta,
}: ReturnType<typeof makeApproval>): Generator<
  SelectEffect | CallEffect | PutEffect,
  void,
  ISwap & ContractTransaction
> {
  const activeSwap = yield select(selectActiveSwap);
  const contract = new Contract(tokenAddress, ERC721ABI, signer) as ERC721;

  const kiwiContractAddress = getKiwiContractAddress(chainId, activeSwap);
  try {
    const tx = yield call(contract.setApprovalForAll, kiwiContractAddress, true);
    yield put(
      addPendingTransaction({
        tx,
        summary: 'Contract approval',
        completeMessage: 'Successfully approved NFT transfer',
        errorMessage: 'NFT approval failed',
      }),
    );
    yield call(tx.wait, 1);
    meta(true);
  } catch (e) {
    addErrorNotification('failed-tx', 'Failed transaction', e.message);
    meta(false);
  }
}

export function* nftSagaWatcher(): Generator {
  yield all([takeEvery(checkApprovals, fetchUserSelectedNFTsApproval), takeEvery(makeApproval, writeApproval)]);
}
